import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/shared/primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { shortDate } from "@/lib/format";

export default function DataSources() {
  const qc = useQueryClient();
  const { data: apps } = useQuery({ queryKey: ["apps-all"], queryFn: () => api.get("/apps") });
  const { data: imports, refetch } = useQuery({ queryKey: ["imports"], queryFn: () => api.get("/imports") });

  const [appId, setAppId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [createUnknown, setCreateUnknown] = useState(false);

  const doPreview = async () => {
    if (!appId || !file) return toast.error("Pick a tool and a CSV file.");
    const form = new FormData();
    form.append("file", file);
    form.append("app_id", appId);
    try {
      const res = await api.post("/imports", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      setMapping(res.data.suggested_mapping || {});
    } catch (e) {
      toast.error(e.message);
    }
  };

  const commit = async () => {
    try {
      const res = await api.post(`/imports/${preview.import_id}/commit`, { mapping, create_unknown: createUnknown });
      toast.success(`Imported ${res.data.rollups_created} rows, ${res.data.seats_created} seats, ${res.data.members_created} new people.`);
      setPreview(null); setFile(null); setMapping({});
      refetch();
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const undo = async (id) => {
    try {
      const res = await api.del(`/imports/${id}`);
      toast.success(`Undone, removed ${res.data.rollups_removed} rows and ${res.data.seats_removed} seats.`);
      refetch();
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const headers = preview?.headers || [];

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <SectionTitle className="mb-1">Import a vendor CSV</SectionTitle>
        <p className="text-[13px] text-nb-ink2 mb-4">Map the columns, preview, then commit. Every import can be undone.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[13px] text-nb-ink2 block mb-1">Tool</label>
            <Select value={appId} onValueChange={setAppId}>
              <SelectTrigger className="w-[220px]" data-testid="import-app-select"><SelectValue placeholder="Choose a tool" /></SelectTrigger>
              <SelectContent>
                {(apps?.data || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[13px] text-nb-ink2 block mb-1">CSV file</label>
            <input data-testid="import-file" type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])}
              className="text-[13px] text-nb-ink2 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border file:border-nb-border file:bg-nb-surface file:text-nb-ink" />
          </div>
          <Button data-testid="import-preview-btn" onClick={doPreview} disabled={!appId || !file}
            className="rounded-[10px] gap-2 bg-nb-navy text-nb-cream font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            <Upload size={15} /> Preview import
          </Button>
        </div>

        {preview && (
          <div className="mt-6 border-t border-nb-border pt-5" data-testid="import-preview">
            <p className="text-[14px] font-medium text-nb-ink mb-3">Map columns ({preview.row_count} rows)</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {preview.mappable_fields.map((field) => (
                <div key={field}>
                  <label className="text-[12.5px] text-nb-ink2 block mb-1">{field.replace(/_/g, " ")}</label>
                  <Select value={mapping[field] || "none"} onValueChange={(v) => setMapping({ ...mapping, [field]: v === "none" ? undefined : v })}>
                    <SelectTrigger className="h-9" data-testid={`map-${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">- not mapped -</SelectItem>
                      {headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="w-full text-[13px]">
                <thead><tr className="text-nb-ink2 text-left border-b border-nb-border">{headers.map((h) => <th key={h} className="py-1.5 pr-4 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {preview.sample_rows.map((r, i) => (
                    <tr key={i} className="border-b border-nb-border last:border-0">{headers.map((h) => <td key={h} className="py-1.5 pr-4 text-nb-ink">{r[h]}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.unknown_members.length > 0 && (
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: "rgba(201,183,156,0.14)" }}>
                <p className="text-[13px] text-nb-ink mb-2">{preview.unknown_members.length} people in this file are not in the directory: {preview.unknown_members.slice(0, 5).join(", ")}{preview.unknown_members.length > 5 ? "…" : ""}</p>
                <label className="flex items-center gap-2 text-[13px] text-nb-ink">
                  <Checkbox checked={createUnknown} onCheckedChange={setCreateUnknown} data-testid="create-unknown" /> Create these people, otherwise skip them
                </label>
              </div>
            )}

            <div className="flex gap-2">
              <Button data-testid="import-commit-btn" onClick={commit} className="rounded-full bg-nb-sage hover:bg-nb-sage/90 text-white">Commit import</Button>
              <Button variant="outline" className="rounded-full" onClick={() => setPreview(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <SectionTitle className="mb-3">Past imports</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead><tr className="text-[13px] text-nb-ink2 text-left border-b border-nb-border"><th className="py-2 font-medium">File</th><th className="py-2 font-medium">Rows</th><th className="py-2 font-medium">Status</th><th className="py-2 font-medium">When</th><th></th></tr></thead>
            <tbody>
              {(imports?.data || []).map((b) => (
                <tr key={b.id} className="border-b border-nb-border last:border-0" data-testid={`import-row-${b.id}`}>
                  <td className="py-3 text-nb-ink">{b.file_name}</td>
                  <td className="py-3 tnum">{b.row_count}</td>
                  <td className="py-3"><span className="text-[12.5px] px-2 py-0.5 rounded-full bg-nb-border/40 text-nb-ink2">{b.status}</span></td>
                  <td className="py-3 text-nb-ink2">{shortDate(b.created_at)}</td>
                  <td className="py-3 text-right">
                    {b.status === "committed" && (
                      <button data-testid={`undo-${b.id}`} onClick={() => undo(b.id)} className="inline-flex items-center gap-1.5 text-[12.5px] text-nb-ink2 hover:text-nb-ink border border-nb-border rounded-full px-3 py-1 transition-colors">
                        <Undo2 size={13} /> Undo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(imports?.data || []).length === 0 && <tr><td colSpan={5} className="py-4 text-[14px] text-nb-ink2">No imports yet. Your committed imports will appear here with an undo option.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
